import campaignCreatorMatchesFetchers from '@/api/fetchers/campaignCreatorMatches';
import type {InvitedCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import mapMatchToCreatorRow, {
  type InvitedMatchApiResponse,
} from '@/views/campaigns/creators-tab/utils/mapMatchToCreatorRow';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page';
import useInfiniteDataQuery from '@/hooks/use-infinite-data-query';

const PER_PAGE = 50;

export const CAMPAIGN_V2_INVITED_CREATORS_BASE_QUERY_KEY =
  'campaignV2InvitedCreators';

interface UseCampaignInvitedCreatorsQueryParams {
  brandId?: string;
  campaignId?: string;
  perPage?: number;
  sort?: string;
  enabled?: boolean;
}

function useCampaignInvitedCreatorsQuery({
  brandId,
  campaignId,
  perPage = PER_PAGE,
  sort,
  enabled = true,
}: UseCampaignInvitedCreatorsQueryParams) {
  const response = useInfiniteDataQuery({
    queryKey: [
      CAMPAIGN_V2_INVITED_CREATORS_BASE_QUERY_KEY,
      {campaignId, perPage, brandId, sort},
    ],
    queryFn: (context: {queryKey: unknown[]; pageParam?: number}) =>
      campaignCreatorMatchesFetchers.invited({
        ...context,
        meta: {
          campaignId,
          page: context.pageParam ?? 1,
          perPage,
          brandId,
          sort,
        },
      }),
    enabled: !!campaignId && enabled,
    keepPreviousData: false,
    getNextPageParam: (lastPage: {
      meta?: {pagination?: {currentPage: number; totalPages: number}};
    }) => {
      const pagination = lastPage?.meta?.pagination;
      if (!pagination) {
        return undefined;
      }
      if (pagination.currentPage >= pagination.totalPages) {
        return undefined;
      }
      return pagination.currentPage + 1;
    },
  });

  const {data} = response;
  const pages = (data as {pages?: {data: InvitedMatchApiResponse[]}[]})?.pages;
  const creators: InvitedCreatorRow[] = pages
    ? pages.flatMap((page) =>
        (page?.data ?? []).map((row) => mapMatchToCreatorRow(row))
      )
    : [];

  const fetchNextPage = useGuardedFetchNextPage(
    response.fetchNextPage,
    response.isFetching
  );

  return {
    creators,
    isLoading: response.isLoading,
    isFetching: response.isFetching,
    isError: response.isError,
    isPreviousData: response.isPreviousData,
    hasNextPage: response.hasNextPage ?? false,
    fetchNextPage,
    refetch: response.refetch,
  };
}

export default useCampaignInvitedCreatorsQuery;
